/**
 * Tournament Listing Page - Core Filter Logic (Typeahead Pattern)
 * Matches profileListingPage.js architecture
 * Handles: State, City, Rating, Table Size with typeahead search
 */

(function () {
  'use strict';

  const wrapper = document.querySelector('.profile-listing-wrapper[data-listing-slug]');
  if (!wrapper) return;

  const normalizeRegionValue = (value) => String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

  const STATE_NAME_MAP = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'District of Columbia'
  };

  const STATE_NAME_TO_ABBREV = Object.keys(STATE_NAME_MAP).reduce((acc, key) => {
    acc[STATE_NAME_MAP[key].toLowerCase()] = key;
    return acc;
  }, {});

  const getStateAbbrev = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    const upper = raw.toUpperCase();
    if (STATE_NAME_MAP[upper]) return upper;

    const mappedFromName = STATE_NAME_TO_ABBREV[normalizeRegionValue(raw)];
    if (mappedFromName) return mappedFromName;

    const normalized = normalizeRegionValue(raw);
    const matched = getAvailableStates().find((option) => normalizeRegionValue(option) === normalized);
    if (matched) {
      const matchedRaw = String(matched).trim();
      const matchedUpper = matchedRaw.toUpperCase();
      if (STATE_NAME_MAP[matchedUpper]) return matchedUpper;
      const matchedFromName = STATE_NAME_TO_ABBREV[normalizeRegionValue(matchedRaw)];
      if (matchedFromName) return matchedFromName;
      return matchedRaw;
    }

    return upper;
  };

  const getStateDisplayName = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    const abbrev = getStateAbbrev(raw);
    if (abbrev && STATE_NAME_MAP[abbrev]) return STATE_NAME_MAP[abbrev];

    const normalized = normalizeRegionValue(raw);
    const matched = getAvailableStates().find((option) => normalizeRegionValue(option) === normalized);
    return String(matched || raw).trim();
  };

  // ============================================================================
  // DOM REFERENCES
  // ============================================================================

  const listingSlug = wrapper.getAttribute('data-listing-slug') || 'tournaments';
  const grid = document.getElementById('tournament-grid');
  const heroSearchInput = document.getElementById('heroSearchInput');
  const activeFiltersContainer = document.getElementById('tournamentActiveFilters');

  // Hidden form inputs (filter state)
  const stateHidden = document.getElementById('state');
  const cityHidden = document.getElementById('city');
  const ratingHidden = document.getElementById('rating');
  const tableSizeHidden = document.getElementById('table_size');
  const buyInMinHidden = document.getElementById('buy_in_min');
  const buyInMaxHidden = document.getElementById('buy_in_max');
  const latitudeInput = document.getElementById('tournament-latitude');
  const longitudeInput = document.getElementById('tournament-longitude');
  const sortSelect = document.getElementById('sort');

  // Typeahead inputs
  const stateInput = document.getElementById('tournamentStateTypeahead');
  const cityInput = document.getElementById('tournamentCityTypeahead');
  const ratingInput = document.getElementById('tournamentRatingTypeahead');
  const sizeInput = document.getElementById('tournamentSizeTypeahead');

  // Typeahead containers
  const stateContainer = document.getElementById('tournamentStateTypeaheadContainer');
  const cityContainer = document.getElementById('tournamentCityTypeaheadContainer');
  const ratingContainer = document.getElementById('tournamentRatingTypeaheadContainer');
  const sizeContainer = document.getElementById('tournamentSizeTypeaheadContainer');

  // Dropdowns (search results)
  const stateDropdown = document.getElementById('tournamentStateTypeahead_dropdown');
  const cityDropdown = document.getElementById('tournamentCityTypeahead_dropdown');
  const ratingDropdown = document.getElementById('tournamentRatingTypeahead_dropdown');
  const sizeDropdown = document.getElementById('tournamentSizeTypeahead_dropdown');

  // Chips (button displays)
  const stateChip = document.getElementById('tournamentStateChip');
  const cityChip = document.getElementById('tournamentCityChip');
  const ratingChip = document.getElementById('tournamentRatingChip');
  const sizeChip = document.getElementById('tournamentSizeChip');

  // Location & sort
  const useLocationBtn = document.getElementById('tournament-use-location-btn');
  const moreBtn = document.getElementById('tournamentMoreBtn');
  const sortButton = document.getElementById('tournamentSortButton');
  const sortDropdown = document.getElementById('tournamentSortDropdown');
  const sortLabel = document.getElementById('tournamentSortButtonLabel');
  const radiusSelect = document.getElementById('tournamentRadiusSelect');
  const gridViewBtn = document.getElementById('tournamentGridViewBtn');
  const mapViewBtn = document.getElementById('tournamentMapViewBtn');
  const mapPanel = document.getElementById('tournamentMapPanel');
  const mapCanvas = document.getElementById('tournamentMapCanvas');
  const mapEmptyState = document.getElementById('tournamentMapEmptyState');
  const mapTabEnabled = window.tournamentMapTabEnabled === true || String(window.tournamentMapTabEnabled).toLowerCase() === 'true';
  const mobileAddTournamentBtn = document.getElementById('mobileAddTournamentBtn');
  const tournamentFilterBar = document.getElementById('tournamentFilterBar');
  const mapboxToken = String(window.tournamentMapboxToken || '').trim();

  // Filter data from template
  const filterData = window.tournamentFilterData || {};
  const filterMetadata = window.filterMetadata || {};
  const stateCityMapping =
    filterData.stateCityMapping ||
    filterMetadata.state_city_mapping ||
    filterMetadata.stateCityMapping ||
    {};

  let tournamentMap = null;
  let tournamentMapMarkers = [];
  let tournamentMapMarkerByLocationKey = new Map();
  let tournamentUserLocationMarker = null;
  let latestRenderedTournaments = [];

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const debounce = (fn, waitMs) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), waitMs);
    };
  };

  const suppressBrowserAutofill = (inputIds) => {
    (inputIds || []).forEach((id) => {
      const input = document.getElementById(id);
      if (!input) return;
      input.setAttribute('autocomplete', 'new-password');
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('autocapitalize', 'none');
      input.setAttribute('spellcheck', 'false');
      input.setAttribute('data-lpignore', 'true');
      input.setAttribute('data-1p-ignore', 'true');
      if (!input.getAttribute('name') || /state|city|country|address/i.test(input.getAttribute('name'))) {
        input.setAttribute('name', `${id}-typeahead`);
      }
    });
  };

  const formatRating = (value) => {
    const raw = String(value || '').trim();
    const map = {
      apa_skill_level: 'APA',
      fargo_rate: 'Fargo',
      bca_rating: 'BCA',
      tap_handicap: 'TAP',
      usapl_rating: 'USAPL',
      vnea_rating: 'VNEA',
      acs_rating: 'ACS',
      in_house_handicap: 'In-house',
      no_handicap_open: 'Open'
    };
    return map[raw] || raw;
  };

  const formatFilterTableSize = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const normalized = raw.toLowerCase().replace(/\s+/g, ' ');
    if (normalized.includes('snooker')) return 'Snooker';
    if (normalized.includes('carom')) return 'Carom';
    const match = normalized.match(/\b(6|7|8|9|10)\s*'?\s*(foot|ft)?\b/);
    if (match) return `${match[1]}'`;
    return raw;
  };

  const formatDateRange = (startValue, endValue) => {
    if (!startValue && !endValue) return '';
    const start = startValue ? new Date(startValue) : null;
    const end = endValue ? new Date(endValue) : null;
    if (!start || Number.isNaN(start.getTime())) return '';
    const startText = start.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
    if (!end || Number.isNaN(end.getTime())) return startText;
    const endText = end.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
    return startText === endText ? startText : `${startText} - ${endText}`;
  };

  const formatTournamentDateLine = (startValue, endValue) => {
    if (!startValue && !endValue) return '';
    const start = startValue ? new Date(startValue) : null;
    if (!start || Number.isNaN(start.getTime())) return '';
    const weekday = start.toLocaleDateString(undefined, { weekday: 'short' });
    const range = formatDateRange(startValue, endValue);
    return `<strong>${escapeHtml(weekday)}</strong> ${escapeHtml(range)}`;
  };

  const titleCase = (value) => String(value || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

  const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

  const normalizeTableLabel = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return '--';
    if (raw.includes('snooker')) return 'Snooker';
    if (raw.includes('carom')) return 'Carom';
    const match = raw.match(/(6|7|8|9)\s*'?\s*(foot|ft)?/);
    if (match) return `${match[1]}'`;
    return titleCase(raw);
  };

  const getSpec = (tournament) => (tournament?.specificationData?.tournaments || {});

  const getRecurringFrequencyLabel = (tournamentSpec) => {
    const raw = String(
      firstDefined(
        tournamentSpec?.recurring_frequency,
        tournamentSpec?.series_frequency,
        tournamentSpec?.recurringFrequency,
        tournamentSpec?.seriesFrequency
      ) || ''
    ).trim().toLowerCase();

    if (!raw) return '';
    if (raw === 'daily') return 'Daily';
    if (raw === 'weekly') return 'Weekly';
    if (raw === 'alternate') return 'Alternate Week';
    if (raw === 'monthly') return 'Monthly';
    return titleCase(raw);
  };

  const getBrackets = (tournamentSpec) => {
    const divisions = Array.isArray(tournamentSpec?.divisions) ? tournamentSpec.divisions : [];
    if (divisions.length > 0) {
      return divisions.flatMap((division) =>
        Array.isArray(division?.brackets) ? division.brackets : []
      );
    }
    return Array.isArray(tournamentSpec?.brackets) ? tournamentSpec.brackets : [];
  };

  const getPlayersLabel = (tournamentSpec) => {
    const brackets = getBrackets(tournamentSpec);
    const bracketMax = brackets
      .map((bracket) => Number(firstDefined(bracket.maxPlayers, bracket.max_players)))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => b - a)[0];

    const min = firstDefined(
      tournamentSpec.min_players,
      tournamentSpec.minimum_players,
      tournamentSpec.players_min,
      tournamentSpec.player_min,
      tournamentSpec.player_count_min,
      tournamentSpec.min_player_count
    );
    const max = firstDefined(
      bracketMax,
      tournamentSpec.max_players,
      tournamentSpec.maximum_players,
      tournamentSpec.players_max,
      tournamentSpec.player_max,
      tournamentSpec.player_count_max,
      tournamentSpec.max_player_count,
      tournamentSpec.maxPlayers
    );
    if (min !== undefined && max !== undefined) return `${min}-${max}`;
    if (max !== undefined) return String(max);
    if (min !== undefined) return String(min);
    return '--';
  };

  const getLevelLabel = (tournament, tournamentSpec) => {
    const ratingLabel = formatRating(tournament.ratingSystemType);
    const brackets = getBrackets(tournamentSpec);
    const bracketRanges = Array.from(new Set(
      brackets
        .map((bracket) => {
          const min = firstDefined(bracket.minRating, bracket.min_rating);
          const max = firstDefined(bracket.maxRating, bracket.max_rating);
          if (min !== undefined && max !== undefined) return `${min}-${max}`;
          if (min !== undefined) return `${min}+`;
          return null;
        })
        .filter(Boolean)
    ));
    const apaLevels = Array.from(new Set(
      brackets
        .flatMap((bracket) => Array.isArray(bracket.apaLevels) ? bracket.apaLevels : [])
        .map((value) => String(value).trim())
        .filter(Boolean)
    ));

    const apaLow = firstDefined(tournamentSpec.apa_low, tournamentSpec.apa_min, tournamentSpec.apa_skill_level_low, tournamentSpec.apa_skill_level_min, tournamentSpec.skill_level_low, tournamentSpec.level_min);
    const apaHigh = firstDefined(tournamentSpec.apa_high, tournamentSpec.apa_max, tournamentSpec.apa_skill_level_high, tournamentSpec.apa_skill_level_max, tournamentSpec.skill_level_high, tournamentSpec.level_max);
    const fargoLow = firstDefined(tournamentSpec.fargo_low, tournamentSpec.fargo_min, tournamentSpec.fargo_rate_low, tournamentSpec.fargo_rate_min);
    const fargoHigh = firstDefined(tournamentSpec.fargo_high, tournamentSpec.fargo_max, tournamentSpec.fargo_rate_high, tournamentSpec.fargo_rate_max);

    if (ratingLabel === 'APA' && apaLow !== undefined && apaHigh !== undefined) return `${apaLow}-${apaHigh}`;
    if (ratingLabel === 'APA' && apaLevels.length) return apaLevels.join(', ');
    if (ratingLabel === 'Fargo' && fargoLow !== undefined && fargoHigh !== undefined) return `${fargoLow}-${fargoHigh}`;
    if (ratingLabel === 'Fargo' && fargoLow !== undefined) return `${fargoLow}+`;
    if (ratingLabel === 'Fargo' && bracketRanges.length) return bracketRanges.join(', ');

    const level = firstDefined(
      tournamentSpec.level,
      tournamentSpec.bracket_level,
      tournamentSpec.skill_level,
      Array.isArray(tournamentSpec.skill_levels) ? tournamentSpec.skill_levels.join(', ') : undefined
    );
    return level !== undefined ? String(level) : 'All';
  };

  const getTableLabel = (tournamentSpec) => {
    const bracketTableLabels = Array.from(new Set(
      getBrackets(tournamentSpec)
        .flatMap((bracket) => Array.isArray(bracket.tableSizes) ? bracket.tableSizes : [])
        .map(normalizeTableLabel)
        .filter((value) => value && value !== '--')
    ));
    if (bracketTableLabels.length > 1) return 'Varies';
    if (bracketTableLabels.length === 1) return bracketTableLabels[0];

    const tableValue = firstDefined(tournamentSpec.table_size, tournamentSpec.tableSize, tournamentSpec.table_sizes);
    if (Array.isArray(tableValue)) {
      const labels = Array.from(new Set(tableValue.map(normalizeTableLabel).filter(Boolean)));
      if (!labels.length) return '--';
      return labels.length > 1 ? 'Varies' : labels[0];
    }
    return normalizeTableLabel(tableValue);
  };

  const formatFeetLabel = (value) => {
    const normalized = normalizeTableLabel(value);
    const cleaned = String(normalized || '').trim();
    if (!cleaned || cleaned === '--') return '--';
    if (/^[6789]$/.test(cleaned)) return `${cleaned}'`;
    return cleaned
      .replace(/(6|7|8|9)\s*-?\s*foot/gi, "$1'")
      .replace(/(6|7|8|9)\s*ft/gi, "$1'")
      .replace(/(6|7|8|9)\s*'/gi, "$1'")
      .replace(/\b(6|7|8|9)\b(?!')/g, "$1'");
  };

  const getGameLabel = (tournamentSpec) => {
    const bracketGames = Array.from(new Set(
      getBrackets(tournamentSpec)
        .map((bracket) => firstDefined(bracket.gameType, bracket.game_type))
        .filter(Boolean)
        .map(titleCase)
    ));
    if (bracketGames.length > 1) return 'Varies';
    if (bracketGames.length === 1) return bracketGames[0];

    const gameValue = firstDefined(tournamentSpec.game_type, tournamentSpec.gameType, tournamentSpec.game, tournamentSpec.discipline);
    if (Array.isArray(gameValue)) return gameValue.length ? gameValue.map(titleCase).join(', ') : '--';
    return gameValue ? titleCase(gameValue) : '--';
  };

  const getCombinedGameLabel = (tournamentSpec) => {
    const brackets = getBrackets(tournamentSpec);
    if (!brackets.length) return getGameLabel(tournamentSpec);

    const gamePairs = Array.from(new Set(
      brackets
        .map((bracket) => {
          const winner = firstDefined(bracket.gameType, bracket.game_type, bracket.winnerGameType, bracket.winner_game_type);
          const loser = firstDefined(bracket.losersGameType, bracket.loserGameType, bracket.losers_game_type, bracket.loser_game_type);
          const winnerLabel = winner ? titleCase(winner) : '';
          const loserLabel = loser ? titleCase(loser) : '';
          if (!winnerLabel && !loserLabel) return null;
          if (winnerLabel && loserLabel) return `${winnerLabel} / ${loserLabel}`;
          return winnerLabel || loserLabel;
        })
        .filter(Boolean)
    ));

    if (!gamePairs.length) return getGameLabel(tournamentSpec);
    if (gamePairs.length > 1) return 'Varies';
    return gamePairs[0];
  };

  const getPrizePoolLabel = (tournamentSpec) => {
    const value = firstDefined(tournamentSpec.prize_payouts, tournamentSpec.prize_pool, tournamentSpec.prizePayouts);
    return value !== undefined ? `$${value}` : '--';
  };

  const getRaceLabel = (tournamentSpec) => {
    const brackets = getBrackets(tournamentSpec);
    if (!brackets.length) return '--';

    const hasVaryBySkill = brackets.some((bracket) =>
      Boolean(firstDefined(bracket.raceMayVaryBySkillLevel, bracket.race_may_vary_by_skill_level))
    );
    if (hasVaryBySkill) return 'Varies by skill level';

    const racePairs = Array.from(new Set(
      brackets
        .map((bracket) => {
          const winner = firstDefined(bracket.winnerRaces, bracket.winnerRace, bracket.winner_races, bracket.winner_race);
          const loser = firstDefined(bracket.loserRaces, bracket.loserRace, bracket.loser_races, bracket.loser_race);
          if (winner === undefined && loser === undefined) return null;
          if (winner !== undefined && loser !== undefined) return `${winner} / ${loser}`;
          return String(winner !== undefined ? winner : loser);
        })
        .filter(Boolean)
    ));

    if (!racePairs.length) return '--';
    if (racePairs.length > 1) return 'Varies';
    return racePairs[0];
  };

  const buildDirectionsUrl = (tournament) => {
    const destinationLabel = [
      tournament.locationName || tournament.title,
      tournament.locationCity,
      getStateDisplayName(tournament.locationState),
      tournament.postalCode,
      tournament.locationCountry,
    ].filter(Boolean).join(', ');

    if (destinationLabel) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destinationLabel)}`;
    }

    if (Number.isFinite(Number(tournament.latitude)) && Number.isFinite(Number(tournament.longitude))) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${tournament.latitude},${tournament.longitude}`)}`;
    }

    return 'https://www.google.com/maps';
  };

  const getParams = () => {
    try {
      return new URLSearchParams(window.location.search);
    } catch (error) {
      return new URLSearchParams();
    }
  };

  const ensureArray = (value) => (Array.isArray(value) ? value : []);

  const getAvailableStates = () => ensureArray(
    filterData.availableStates ||
    filterData.available_states ||
    filterMetadata.available_states ||
    filterMetadata.availableStates
  );

  const getAvailableCities = () => ensureArray(
    filterData.availableCities ||
    filterData.available_cities ||
    filterMetadata.available_cities ||
    filterMetadata.availableCities
  );

  const getAvailableRatings = () => ensureArray(
    filterData.availableRatings ||
    filterData.available_rating_systems ||
    filterMetadata.available_rating_systems ||
    filterMetadata.availableRatings
  );

  const getAvailableTableSizes = () => ensureArray(
    filterData.availableTableSizes ||
    filterData.available_table_sizes ||
    filterMetadata.available_table_sizes ||
    filterMetadata.availableTableSizes
  );

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const escapeJsString = (value) => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'");

  const toFiniteNumber = (value) => {
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const calculateDistanceMiles = (lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => deg * (Math.PI / 180);
    const earthRadiusMiles = 3958.8;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMiles * c;
  };

  const getTournamentCoordinates = (tournament) => {
    if (!tournament || typeof tournament !== 'object') return null;

    const lat = toFiniteNumber(
      tournament.latitude ??
      tournament.lat ??
      tournament.locationLatitude ??
      tournament.location_latitude ??
      tournament.location?.latitude ??
      tournament.location?.lat ??
      tournament.profileLatitude ??
      tournament.profile_latitude ??
      tournament.specificationData?.tournaments?.location_latitude ??
      tournament.specificationData?.tournaments?.latitude ??
      tournament.profile?.latitude
    );
    const lng = toFiniteNumber(
      tournament.longitude ??
      tournament.lng ??
      tournament.locationLongitude ??
      tournament.location_longitude ??
      tournament.location?.longitude ??
      tournament.location?.lng ??
      tournament.profileLongitude ??
      tournament.profile_longitude ??
      tournament.specificationData?.tournaments?.location_longitude ??
      tournament.specificationData?.tournaments?.longitude ??
      tournament.profile?.longitude
    );

    if (lat === null || lng === null) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    if (Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001) return null;
    return { lat, lng };
  };

  const getTournamentMapLocationKey = (tournament) => {
    const profileId = String(tournament?.profile?.id || tournament?.profileId || tournament?.profile_id || '').trim();
    if (profileId) {
      return `profile:${profileId}`;
    }

    const locationEntityId = String(
      tournament?.locationId ||
      tournament?.location_id ||
      tournament?.location?.id ||
      ''
    ).trim();
    if (locationEntityId) {
      return `location:${locationEntityId}`;
    }

    const semanticLocationKey = [
      String(tournament?.locationName || '').trim().toLowerCase(),
      String(tournament?.locationCity || '').trim().toLowerCase(),
      String(getStateDisplayName(tournament?.locationState) || '').trim().toLowerCase(),
      String(tournament?.postalCode || '').trim().toLowerCase(),
      String(tournament?.locationCountry || '').trim().toLowerCase(),
    ].filter(Boolean).join('|');
    if (semanticLocationKey) {
      return `name:${semanticLocationKey}`;
    }

    const coords = getTournamentCoordinates(tournament);
    if (coords) {
      return `${coords.lat.toFixed(5)}|${coords.lng.toFixed(5)}`;
    }

    return null;
  };

  const applyClientDistanceFilter = (items) => {
    const list = Array.isArray(items) ? items : [];
    const usesDistance = filterState.sort === 'distance' || filterState.sort === 'date-distance';
    if (!usesDistance) return list;

    const userLat = toFiniteNumber(filterState.latitude);
    const userLng = toFiniteNumber(filterState.longitude);
    if (userLat === null || userLng === null) return list;

    const parsedRadius = Number.parseFloat(String(filterState.radius_miles || '25'));
    const radiusMiles = Number.isFinite(parsedRadius) && parsedRadius > 0 ? parsedRadius : 25;

    const enriched = list
      .map((tournament) => {
        const coords = getTournamentCoordinates(tournament);
        if (!coords) return null;
        return {
          ...tournament,
          distance: calculateDistanceMiles(userLat, userLng, coords.lat, coords.lng),
        };
      })
      .filter(Boolean)
      .filter((tournament) => tournament.distance <= radiusMiles);

    if (filterState.sort === 'distance') {
      enriched.sort((a, b) => a.distance - b.distance);
      return enriched;
    }

    if (filterState.sort === 'date-distance') {
      enriched.sort((a, b) => {
        const aTime = new Date(a.tournamentStartDate || a.tournament_start_date || 0).getTime();
        const bTime = new Date(b.tournamentStartDate || b.tournament_start_date || 0).getTime();
        const primary = aTime - bTime;
        if (primary !== 0) return primary;
        return a.distance - b.distance;
      });
    }

    return enriched;
  };

  const setMapEmptyMessage = (message) => {
    if (!mapEmptyState) return;
    mapEmptyState.textContent = message;
    mapEmptyState.style.display = 'block';
  };

  const clearTournamentMapMarkers = () => {
    tournamentMapMarkers.forEach((marker) => marker.remove());
    tournamentMapMarkers = [];
    tournamentMapMarkerByLocationKey = new Map();
  };

  const clearTournamentUserLocationMarker = () => {
    if (!tournamentUserLocationMarker) return;
    tournamentUserLocationMarker.remove();
    tournamentUserLocationMarker = null;
  };

  const addTournamentUserLocationMarker = (bounds) => {
    if (!tournamentMap || !window.mapboxgl) return false;

    const latitude = toFiniteNumber(filterState.latitude);
    const longitude = toFiniteNumber(filterState.longitude);
    if (latitude === null || longitude === null) {
      clearTournamentUserLocationMarker();
      return false;
    }

    clearTournamentUserLocationMarker();

    tournamentUserLocationMarker = new window.mapboxgl.Marker({ color: '#dc2626' })
      .setLngLat([longitude, latitude])
      .setPopup(new window.mapboxgl.Popup({ offset: 18 }).setHTML('<div class="tournament-map-popup"><h4>Your location</h4></div>'))
      .addTo(tournamentMap);

    if (bounds) bounds.extend([longitude, latitude]);
    return true;
  };

  const closeAllTournamentMapPopups = (exceptMarker = null) => {
    tournamentMapMarkers.forEach((marker) => {
      if (!marker || marker === exceptMarker) return;
      const popup = marker.getPopup ? marker.getPopup() : null;
      if (popup && popup.isOpen()) popup.remove();
    });
  };

  const setActiveTournamentMapListItem = (index) => {
    const listEl = document.getElementById('tournamentMapList');
    if (!listEl) return;

    listEl.querySelectorAll('.tournament-map-list-item').forEach((item) => item.classList.remove('is-active'));
    if (!Number.isFinite(index) || index < 0) return;

    const target = listEl.querySelector(`.tournament-map-list-item[data-map-index="${index}"]`);
    if (!target) return;
    target.classList.add('is-active');
    target.scrollIntoView({ block: 'nearest' });
  };

  const ensureTournamentMapListPanel = () => {
    if (!mapPanel || !mapTabEnabled) return null;

    let panel = mapPanel.querySelector('#tournamentMapListPanel');
    if (panel) return panel;

    panel = document.createElement('aside');
    panel.id = 'tournamentMapListPanel';
    panel.className = 'tournament-map-list-panel';
    panel.setAttribute('aria-label', 'Tournaments in map view');
    panel.innerHTML = `
      <div class="tournament-map-list-header">
        <span id="tournamentMapListCount">0 tournaments</span>
        <button type="button" id="tournamentMapBackToGridBtn" class="tournament-map-list-header__grid-btn" aria-label="Switch to grid view">Grid</button>
      </div>
      <div id="tournamentMapList" class="tournament-map-list"></div>
    `;

    const switchToGridBtn = panel.querySelector('#tournamentMapBackToGridBtn');
    if (switchToGridBtn) {
      switchToGridBtn.addEventListener('click', () => {
        if (typeof window.setTournamentViewMode === 'function') {
          window.setTournamentViewMode('grid');
        }
      });
    }

    mapPanel.appendChild(panel);
    return panel;
  };

  const renderTournamentMapList = (items) => {
    const panel = ensureTournamentMapListPanel();
    if (!panel) return;

    const countEl = panel.querySelector('#tournamentMapListCount');
    const listEl = panel.querySelector('#tournamentMapList');
    if (!countEl || !listEl) return;

    const mappableTournaments = (Array.isArray(items) ? items : []).filter((item) => Boolean(getTournamentCoordinates(item)));
    countEl.textContent = `${mappableTournaments.length} ${mappableTournaments.length === 1 ? 'tournament' : 'tournaments'}`;

    if (mappableTournaments.length === 0) {
      listEl.innerHTML = '<div class="tournament-map-list-empty">No mappable tournament locations for the current filters.</div>';
      return;
    }

    listEl.innerHTML = mappableTournaments.map((tournament, index) => {
      const tournamentDateLabel = formatTournamentDateLine(tournament.tournamentStartDate, tournament.tournamentEndDate);
      const stateLabel = getStateDisplayName(tournament.locationState);
      const detailsHref = `/pages/${listingSlug}/${escapeHtml(tournament.slug || '')}`;
      const directionsUrl = buildDirectionsUrl(tournament);
      const locationKey = getTournamentMapLocationKey(tournament) || '';
      const tournamentSpec = getSpec(tournament);
      const tableLabel = getTableLabel(tournamentSpec);
      const tableLabelDisplay = formatFeetLabel(tableLabel);
      const gameLabel = getCombinedGameLabel(tournamentSpec);
      const buyInLabel = tournament.buyIn != null ? `$${tournament.buyIn}` : '--';

      return `
        <div class="tournament-map-list-item" data-map-index="${index}" data-map-location-key="${escapeHtml(locationKey)}">
          <button type="button" class="tournament-map-list-item__focus" data-map-focus="${index}">
            <div class="tournament-map-list-item__title">${escapeHtml(tournament.title || 'Tournament')}</div>
            <div class="tournament-map-list-item__meta">${tournamentDateLabel || ''}</div>
            <div class="tournament-map-list-item__meta">${escapeHtml(tournament.locationName || '')}</div>
            <div class="tournament-map-list-item__meta">${escapeHtml(stateLabel || '')}</div>
          </button>
          <div class="tournament-map-list-item__pills">
            <span class="map-pill map-pill-buyin">Buy-in ${escapeHtml(buyInLabel)}</span>
            <span class="map-pill map-pill-game">${escapeHtml(gameLabel)}</span>
            <span class="map-pill map-pill-table">${escapeHtml(tableLabelDisplay)}</span>
          </div>
          <div class="tournament-map-list-item__actions" data-map-actions="${index}">
            <a class="tournament-card__direction-btn" target="_blank" rel="noopener" href="${directionsUrl}" aria-label="Get directions">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>
            </a>
            <a class="tournament-card__details-btn" href="${detailsHref}">View Details</a>
          </div>
        </div>
      `;
    }).join('');

    listEl.querySelectorAll('.tournament-map-list-item__focus').forEach((node) => {
      node.addEventListener('click', () => {
        const index = Number(node.getAttribute('data-map-focus'));
        if (!Number.isFinite(index) || index < 0 || !tournamentMap) return;
        const target = mappableTournaments[index];
        const coords = getTournamentCoordinates(target);
        if (!coords) return;
        const locationKey = getTournamentMapLocationKey(target);

        setActiveTournamentMapListItem(index);
        tournamentMap.easeTo({ center: [coords.lng, coords.lat], zoom: Math.max(tournamentMap.getZoom(), 11) });
        const marker = locationKey ? tournamentMapMarkerByLocationKey.get(locationKey) : null;
        if (marker) {
          closeAllTournamentMapPopups(marker);
          const popup = marker.getPopup ? marker.getPopup() : null;
          if (popup && !popup.isOpen()) popup.addTo(tournamentMap);
        }
      });
    });
  };

  const buildTournamentPopupHtml = (tournament) => {
    const locationAddress = [
      tournament.locationCity,
      getStateDisplayName(tournament.locationState),
      tournament.postalCode
    ].filter(Boolean).join(', ');

    return `
      <div class="tournament-map-popup">
        <h4>${escapeHtml(tournament.title || 'Tournament')}</h4>
        ${tournament.locationName ? `<div class="tournament-map-popup__line">${escapeHtml(tournament.locationName)}</div>` : ''}
        ${locationAddress ? `<div class="tournament-map-popup__line">${escapeHtml(locationAddress)}</div>` : ''}
        <a class="tournament-map-popup__link" href="/pages/${listingSlug}/${escapeHtml(tournament.slug || '')}">View Details</a>
      </div>
    `;
  };

  const buildTournamentLocationPopupHtml = (tournamentsAtLocation) => {
    const list = Array.isArray(tournamentsAtLocation) ? tournamentsAtLocation : [];
    const first = list[0] || {};
    const locationAddress = [
      first.locationCity,
      getStateDisplayName(first.locationState),
      first.postalCode,
    ].filter(Boolean).join(', ');

    const title = first.locationName || first.title || 'Location';
    const tournamentLinks = list
      .slice(0, 6)
      .map((tournament) => {
        const slug = escapeHtml(tournament.slug || '');
        const name = escapeHtml(tournament.title || 'Tournament');
        return `<div class="tournament-map-popup__line"><a class="tournament-map-popup__link" href="/pages/${listingSlug}/${slug}">${name}</a></div>`;
      })
      .join('');

    const moreCount = list.length > 6 ? list.length - 6 : 0;
    const moreLine = moreCount > 0 ? `<div class="tournament-map-popup__line">+${moreCount} more tournaments</div>` : '';

    return `
      <div class="tournament-map-popup">
        <h4>${escapeHtml(title)}</h4>
        ${locationAddress ? `<div class="tournament-map-popup__line">${escapeHtml(locationAddress)}</div>` : ''}
        ${tournamentLinks}
        ${moreLine}
      </div>
    `;
  };

  const ensureTournamentMap = () => {
    if (!mapPanel || !mapCanvas) return false;

    if (!mapTabEnabled) {
      setMapEmptyMessage('Map view coming soon. Stay on Grid view for now.');
      return false;
    }

    if (!mapboxToken || !mapboxToken.startsWith('pk.')) {
      setMapEmptyMessage('Map unavailable: add a valid Mapbox public token (pk...) in this section settings.');
      return false;
    }

    if (!window.mapboxgl) {
      setMapEmptyMessage('Map failed to load. Please refresh and try again.');
      return false;
    }

    if (tournamentMap) return true;

    window.mapboxgl.accessToken = mapboxToken;
    tournamentMap = new window.mapboxgl.Map({
      container: mapCanvas,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-98.5795, 39.8283],
      zoom: 3.2
    });
    tournamentMap.on('error', (event) => {
      const rawMessage = String(event?.error?.message || event?.message || '').trim();
      if (rawMessage) {
        setMapEmptyMessage(`Map tiles failed: ${rawMessage}`);
      } else {
        setMapEmptyMessage('Map tiles failed to load. Check your Mapbox token and allowed URLs for this domain.');
      }
    });
    tournamentMap.on('load', () => {
      if (mapEmptyState && mapEmptyState.textContent.includes('tiles failed')) {
        mapEmptyState.style.display = 'none';
        mapEmptyState.textContent = '';
      }
    });
    tournamentMap.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
    return true;
  };

  const updateTournamentMapMarkers = (items) => {
    latestRenderedTournaments = Array.isArray(items) ? items : [];
    renderTournamentMapList(latestRenderedTournaments);

    if (!ensureTournamentMap()) return;
    if (!tournamentMap) return;

    clearTournamentMapMarkers();
    clearTournamentUserLocationMarker();

    const bounds = new window.mapboxgl.LngLatBounds();

    const mappableTournaments = latestRenderedTournaments.filter((tournament) => Boolean(getTournamentCoordinates(tournament)));
    const firstListIndexByLocationKey = new Map();
    mappableTournaments.forEach((tournament, index) => {
      const key = getTournamentMapLocationKey(tournament);
      if (key && !firstListIndexByLocationKey.has(key)) {
        firstListIndexByLocationKey.set(key, index);
      }
    });

    const locationGroups = new Map();
    latestRenderedTournaments.forEach((tournament) => {
      const coords = getTournamentCoordinates(tournament);
      if (!coords) return;

      const locationKey = getTournamentMapLocationKey(tournament);
      if (!locationKey) return;

      if (!locationGroups.has(locationKey)) {
        locationGroups.set(locationKey, {
          key: locationKey,
          coords,
          tournaments: [],
          firstListIndex: null,
        });
      }

      const group = locationGroups.get(locationKey);
      group.tournaments.push(tournament);

      if (group.firstListIndex === null) {
        const listIndex = firstListIndexByLocationKey.get(locationKey);
        group.firstListIndex = Number.isFinite(listIndex) ? listIndex : null;
      }
    });

    locationGroups.forEach((group) => {
      const marker = new window.mapboxgl.Marker({ color: '#e830c9' })
        .setLngLat([group.coords.lng, group.coords.lat])
        .setPopup(new window.mapboxgl.Popup({ offset: 20 }).setHTML(buildTournamentLocationPopupHtml(group.tournaments)))
        .addTo(tournamentMap);

      const popup = marker.getPopup ? marker.getPopup() : null;
      if (popup) {
        popup.on('open', () => {
          closeAllTournamentMapPopups(marker);
          if (Number.isFinite(group.firstListIndex) && group.firstListIndex >= 0) {
            setActiveTournamentMapListItem(group.firstListIndex);
          }
        });
      }

      tournamentMapMarkers.push(marker);
      tournamentMapMarkerByLocationKey.set(group.key, marker);
      bounds.extend([group.coords.lng, group.coords.lat]);
    });

    const validCount = locationGroups.size;

    const hasUserMarker = addTournamentUserLocationMarker(bounds);

    if (validCount === 0) {
      setMapEmptyMessage('No mappable tournament locations for the current filters.');
      if (hasUserMarker) {
        const center = bounds.getCenter();
        tournamentMap.easeTo({ center, zoom: 9.5 });
      }
      return;
    }

    if (mapEmptyState) {
      mapEmptyState.style.display = 'none';
      mapEmptyState.textContent = '';
    }

    if (validCount === 1) {
      const center = bounds.getCenter();
      tournamentMap.easeTo({ center, zoom: 11 });
    } else {
      tournamentMap.fitBounds(bounds, { padding: 40, maxZoom: 11 });
    }
  };

  const isVisibleElement = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 20 && rect.height > 20 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight;
  };

  const isBottomRightOverlay = (rect) => {
    if (!rect) return false;
    return (
      rect.width >= 28 &&
      rect.height >= 28 &&
      rect.right >= window.innerWidth * 0.6 &&
      rect.bottom >= window.innerHeight * 0.6
    );
  };

  const getLikelyChatElement = () => {
    const selectors = [
      '#ShopifyChat',
      '#shopify-chat',
      '[id*="shopify-chat" i]',
      '[class*="shopify-chat" i]',
      '[data-shopify-chat]',
      'iframe[src*="shopify" i][src*="chat" i]',
      'iframe[id*="chat" i]',
      'iframe[class*="chat" i]',
      '[aria-label*="chat" i]',
      '[class*="chat-launcher" i]',
      '[id*="chat-launcher" i]',
      'button[title*="chat" i]'
    ];

    const candidates = [];
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => candidates.push(element));
    });

    // Fallback: detect generic fixed/sticky bottom-right launchers even when vendor markup is unknown.
    document.querySelectorAll('button, a, iframe, div').forEach((element) => {
      if (!isVisibleElement(element)) return;
      if (mobileAddTournamentBtn && element === mobileAddTournamentBtn) return;
      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;

      const rect = element.getBoundingClientRect();
      if (rect.width < 36 || rect.height < 36) return;
      if (rect.width > 420 || rect.height > 220) return;
      if (rect.right < window.innerWidth * 0.65) return;
      if (rect.bottom < window.innerHeight * 0.65) return;

      candidates.push(element);
    });

    let best = null;
    let bestScore = -1;

    candidates.forEach((element) => {
      if (!isVisibleElement(element)) return;
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      const marker = `${element.id || ''} ${element.className || ''} ${element.getAttribute('aria-label') || ''} ${element.getAttribute('title') || ''} ${element.getAttribute('name') || ''} ${element.textContent || ''} ${element.tagName || ''}`.toLowerCase();

      let score = 0;
      if (/(chat|inbox|message|support|help|intercom|zendesk|tidio|gorgias|crisp)/i.test(marker)) score += 45;
      if (element.tagName === 'IFRAME') score += 15;
      if (style.position === 'fixed' || style.position === 'sticky') score += 15;
      if (rect.right >= window.innerWidth * 0.6) score += 15;
      if (rect.bottom >= window.innerHeight * 0.6) score += 15;
      if (rect.width >= 100 && rect.height >= 44) score += 10;

      if (score > bestScore) {
        bestScore = score;
        best = element;
      }
    });

    // Last-resort heuristic: inspect stacked elements in the bottom-right corner.
    if (!best) {
      const samplePoints = [
        [window.innerWidth - 16, window.innerHeight - 16],
        [window.innerWidth - 28, window.innerHeight - 28],
        [window.innerWidth - 44, window.innerHeight - 44],
      ];

      for (const [x, y] of samplePoints) {
        const stack = typeof document.elementsFromPoint === 'function' ? document.elementsFromPoint(x, y) : [];
        const overlay = stack.find((element) => {
          if (!element || element === document.documentElement || element === document.body) return false;
          if (mobileAddTournamentBtn && (element === mobileAddTournamentBtn || mobileAddTournamentBtn.contains(element))) return false;
          if (!isVisibleElement(element)) return false;

          const style = window.getComputedStyle(element);
          if (style.position !== 'fixed' && style.position !== 'sticky') return false;

          const rect = element.getBoundingClientRect();
          if (rect.width < 28 || rect.height < 28) return false;
          return isBottomRightOverlay(rect);
        });

        if (overlay) {
          best = overlay;
          bestScore = 999;
          break;
        }
      }
    }

    if (!best || bestScore < 35) return null;
    const bestRect = best.getBoundingClientRect();
    return isBottomRightOverlay(bestRect) ? best : null;
  };

  const updateMobileAddTournamentButtonPosition = () => {
    if (!mobileAddTournamentBtn) return;

    const btnStyle = window.getComputedStyle(mobileAddTournamentBtn);
    const btnRect = mobileAddTournamentBtn.getBoundingClientRect();
    const isButtonVisible = btnStyle.display !== 'none' && btnStyle.visibility !== 'hidden' && btnRect.width > 0 && btnRect.height > 0;
    if (!isButtonVisible) {
      mobileAddTournamentBtn.style.bottom = '';
      return;
    }

    const defaultBottom = 16;
    let computedBottom = defaultBottom;
    const chatElement = getLikelyChatElement();

    // Keep CTA clear of chat launchers/widgets if present in the lower-right corner.
    if (chatElement) {
      const rect = chatElement.getBoundingClientRect();
      const horizontalOverlap = rect.right >= (btnRect.left - 12) && rect.left <= (btnRect.right + 12);
      const isInLowerViewport = rect.top >= (window.innerHeight * 0.4);
      if (!horizontalOverlap || !isInLowerViewport) {
        mobileAddTournamentBtn.style.bottom = `calc(${defaultBottom}px + env(safe-area-inset-bottom))`;
        return;
      }
      const gap = 12;
      const aboveChatBottom = Math.max(defaultBottom, Math.ceil((window.innerHeight - rect.top) + gap));
      computedBottom = aboveChatBottom;
    }

    mobileAddTournamentBtn.style.bottom = `calc(${computedBottom}px + env(safe-area-inset-bottom))`;
  };

  const setupMobileAddTournamentButton = () => {
    if (!mobileAddTournamentBtn) return;

    const debouncedUpdate = debounce(updateMobileAddTournamentButtonPosition, 120);
    updateMobileAddTournamentButtonPosition();

    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', debouncedUpdate);
    window.addEventListener('scroll', debouncedUpdate, { passive: true });

    if (window.MutationObserver && document.body) {
      const observer = new MutationObserver(debouncedUpdate);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden']
      });
    }

    window.setInterval(updateMobileAddTournamentButtonPosition, 1500);
  };

  const setupStickyTopBar = () => {
    if (!tournamentFilterBar) return;

    const firstFilterTriggerBtn = tournamentFilterBar.querySelector('.filter-trigger-btn');

    const syncStickyState = () => {
      const anchorRect = (firstFilterTriggerBtn || tournamentFilterBar).getBoundingClientRect();
      const hasReachedStickyPoint = anchorRect.top <= 0;

      tournamentFilterBar.classList.toggle('is-stuck', hasReachedStickyPoint && window.scrollY > 0);
    };

    const onViewportChange = debounce(() => {
      syncStickyState();
    }, 120);

    syncStickyState();

    window.addEventListener('scroll', syncStickyState, { passive: true });
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
  };

  // ============================================================================
  // TYPEAHEAD LOGIC
  // ============================================================================

  // Toggle typeahead container visibility
  window.toggleTournamentTypeaheadField = function (inputId, event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    let container;

    if (inputId === 'tournamentStateTypeahead') container = stateContainer;
    else if (inputId === 'tournamentCityTypeahead') container = cityContainer;
    else if (inputId === 'tournamentRatingTypeahead') container = ratingContainer;
    else if (inputId === 'tournamentSizeTypeahead') container = sizeContainer;

    if (!container) return;

    // Close others
    [stateContainer, cityContainer, ratingContainer, sizeContainer].forEach((c) => {
      if (c && c !== container) c.style.display = 'none';
    });

    // Toggle current
    const isOpen = container.style.display !== 'none';
    container.style.display = isOpen ? 'none' : 'block';

    // Focus input if opening
    if (!isOpen) {
      const input = document.getElementById(inputId);
      if (input) {
        input.focus();
        input.value = '';
      }
      window.handleTournamentTypeaheadInput(inputId);
    }
  };

  // Handle typeahead search input
  window.handleTournamentTypeaheadInput = function (inputId) {
    const input = document.getElementById(inputId);
    if (!input) return;

    const value = input.value.trim().toLowerCase();
    let dropdown;
    let allOptions = [];

    // Get options based on input type
    if (inputId === 'tournamentStateTypeahead') {
      dropdown = stateDropdown;
      const stateCodes = getAvailableStates()
        .map((s) => getStateAbbrev(s) || s)
        .filter(Boolean);
      allOptions = Array.from(new Set(stateCodes))
        .map((code) => ({ key: code, label: getStateDisplayName(code) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    } else if (inputId === 'tournamentCityTypeahead') {
      dropdown = cityDropdown;
      let cities = [];

      // If states are selected, show only cities from those states
      if (filterState.state.length > 0) {
        filterState.state.forEach(stateCode => {
          if (stateCityMapping[stateCode]) {
            cities = cities.concat(stateCityMapping[stateCode]);
          }
        });
        // Remove duplicates
        cities = Array.from(new Set(cities));
      } else {
        // If no states selected, show all cities
        cities = getAvailableCities();
      }

      allOptions = cities
        .map((c) => ({ key: c, label: c }))
        .sort((a, b) => a.label.localeCompare(b.label));
    } else if (inputId === 'tournamentRatingTypeahead') {
      dropdown = ratingDropdown;
      allOptions = getAvailableRatings()
        .map((r) => ({ key: r, label: formatRating(r) }));
    } else if (inputId === 'tournamentSizeTypeahead') {
      dropdown = sizeDropdown;
      const formattedSizes = getAvailableTableSizes()
        .map((size) => {
          const key = String(size || '').trim();
          const label = formatFilterTableSize(key);
          return {
            key,
            label,
            searchText: `${key} ${label}`.toLowerCase()
          };
        })
        .filter((opt) => opt.key);

      allOptions = Array.from(
        formattedSizes.reduce((map, option) => {
          const dedupeKey = option.label.toLowerCase();
          if (!map.has(dedupeKey)) map.set(dedupeKey, option);
          return map;
        }, new Map()).values()
      );
    }

    if (!dropdown) return;

    // Show full option list on focus/click; filter once user types.
    const filtered = value.length === 0
      ? allOptions
      : allOptions.filter((opt) => {
        const searchText = String(opt.searchText || `${opt.label} ${opt.key}`).toLowerCase();
        return searchText.includes(value);
      });

    // Render dropdown
    dropdown.innerHTML = filtered
      .slice(0, 20)
      .map(
        (opt) =>
          `<div style="padding: 8px; cursor: pointer; border-radius: 4px;" onclick="selectTournamentFilterOption('${escapeJsString(inputId)}', '${escapeJsString(opt.key)}', '${escapeJsString(opt.label)}')">${escapeHtml(opt.label)}</div>`
      )
      .join('');

    dropdown.style.display = filtered.length > 0 ? 'block' : 'none';
  };

  // Handle focus (show options)
  window.handleTournamentTypeaheadFocus = function (inputId) {
    const input = document.getElementById(inputId);
    if (!input) return;
    handleTournamentTypeaheadInput(inputId);
  };

  // Handle Enter key
  window.handleTournamentTypeaheadKeydown = function (e, inputId) {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyFilters();
    }
  };

  // Select option from dropdown (MULTI-SELECT with toggle)
  window.selectTournamentFilterOption = function (inputId, key, label) {
    if (inputId === 'tournamentStateTypeahead') {
      // Toggle: if already selected, remove it; otherwise add it
      if (filterState.state.includes(key)) {
        filterState.state = filterState.state.filter(v => v !== key);
      } else {
        filterState.state.push(key);
      }
      if (stateInput) stateInput.value = '';
      updateSelectedPills('tournamentStateSelectedPills', filterState.state, 'state');
      updateDynamicCityOptions();
    } else if (inputId === 'tournamentCityTypeahead') {
      // Toggle: if already selected, remove it; otherwise add it
      if (filterState.city.includes(key)) {
        filterState.city = filterState.city.filter(v => v !== key);
      } else {
        filterState.city.push(key);
      }
      if (cityInput) cityInput.value = '';
      updateSelectedPills('tournamentCitySelectedPills', filterState.city, 'city');
    } else if (inputId === 'tournamentRatingTypeahead') {
      // Toggle: if already selected, remove it; otherwise add it
      if (filterState.rating.includes(key)) {
        filterState.rating = filterState.rating.filter(v => v !== key);
      } else {
        filterState.rating.push(key);
      }
      if (ratingInput) ratingInput.value = '';
      updateSelectedPills('tournamentRatingSelectedPills', filterState.rating, 'rating');
    } else if (inputId === 'tournamentSizeTypeahead') {
      // Toggle: if already selected, remove it; otherwise add it
      if (filterState.table_size.includes(key)) {
        filterState.table_size = filterState.table_size.filter(v => v !== key);
      } else {
        filterState.table_size.push(key);
      }
      if (sizeInput) sizeInput.value = '';
      updateSelectedPills('tournamentSizeSelectedPills', filterState.table_size, 'table_size');
    }

    updateChipDisplay();
    updateHiddenInputs();
    applyFilters();
  };

  // Update city options when state changes
  const updateDynamicCityOptions = () => {
    // When state selection changes, filter cities to only show cities from selected states
    if (filterState.state.length > 0) {
      // Get all cities from selected states
      let allCities = [];
      filterState.state.forEach(stateCode => {
        if (stateCityMapping[stateCode]) {
          allCities = allCities.concat(stateCityMapping[stateCode]);
        }
      });
      // Remove duplicates
      allCities = Array.from(new Set(allCities));

      // Remove any selected cities that are not in the new list
      filterState.city = filterState.city.filter(city => allCities.includes(city));
      updateSelectedPills('tournamentCitySelectedPills', filterState.city, 'city');
    }
  };

  const updateCityOptions = () => {
    const selectedState = stateHidden ? stateHidden.value : '';
    let cities = [];

    if (selectedState && stateCityMapping[selectedState]) {
      cities = stateCityMapping[selectedState];
    } else {
      cities = getAvailableCities();
    }

    if (cityInput) {
      cityInput.placeholder = cities.length > 0 ? 'Search city...' : 'No cities available';
    }
  };

  // Update pills display for multi-select
  function updateSelectedPills(containerId, values, type) {
    const container = document.getElementById(containerId);
    if (!container) return;

    const chipMap = {
      'state': 'tournamentStateChip',
      'city': 'tournamentCityChip',
      'rating': 'tournamentRatingChip',
      'table_size': 'tournamentSizeChip'
    };

    const labels = {
      'state': 'State',
      'city': 'City',
      'rating': 'Rating',
      'table_size': 'Table Size'
    };

    const chip = document.getElementById(chipMap[type]);

    // Render pills in dropdown container
    if (values.length > 0) {
      const containerHtml = values.map(value => {
        let displayLabel = value;
        if (type === 'state') {
          displayLabel = getStateDisplayName(value);
        } else if (type === 'rating') {
          displayLabel = formatRating(value);
        } else if (type === 'table_size') {
          displayLabel = formatFilterTableSize(value);
        }

        return `
          <div class="filter-pill">
            <span>${escapeHtml(displayLabel)}</span>
            <button type="button" class="remove-pill-btn" data-type="${escapeHtml(type)}" data-value="${escapeHtml(value)}">&times;</button>
          </div>
        `;
      }).join('');

      container.innerHTML = containerHtml;

      // Bind pill remove events via DOM APIs so quoted values like 7' never break JS parsing.
      container.querySelectorAll('.remove-pill-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const pillType = btn.getAttribute('data-type') || '';
          const pillValue = btn.getAttribute('data-value') || '';
          window.removeTournamentPill(pillType, pillValue);
        });
      });

      // Update chip button to show first item + more indicator
      if (chip) {
        let firstLabel = values[0];
        if (type === 'state') {
          firstLabel = getStateDisplayName(values[0]);
        } else if (type === 'rating') {
          firstLabel = formatRating(values[0]);
        } else if (type === 'table_size') {
          firstLabel = formatFilterTableSize(values[0]);
        }

        chip.textContent = '';
        const wrapper = document.createElement('div');
        wrapper.style.display = 'inline-flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.gap = '6px';
        wrapper.style.background = 'transparent';

        const pill = document.createElement('div');
        pill.style.display = 'inline-flex';
        pill.style.alignItems = 'center';
        pill.style.gap = '4px';
        pill.style.padding = '4px 8px';
        pill.style.background = '#E8F5E9';
        pill.style.borderRadius = '16px';
        pill.style.fontSize = '13px';
        pill.style.border = '1px solid #81C784';
        pill.style.whiteSpace = 'nowrap';
        pill.textContent = firstLabel + ' ×';

        wrapper.appendChild(pill);

        if (values.length > 1) {
          const more = document.createElement('span');
          more.style.fontWeight = '500';
          more.style.color = '#1d4ed8';
          more.style.fontSize = '13px';
          more.style.whiteSpace = 'nowrap';
          more.textContent = `+${values.length - 1}`;
          wrapper.appendChild(more);
        }

        chip.appendChild(wrapper);
      }
    } else {
      // No pills selected
      container.innerHTML = '';
      if (chip) {
        chip.textContent = labels[type] || type;
      }
    }
  }

  // Remove pill function
  const decodeHtmlEntities = (value) => {
    const raw = String(value || '');
    if (!raw || raw.indexOf('&') === -1) return raw;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = raw;
    return textarea.value;
  };

  window.removeTournamentPill = function (type, value) {
    value = decodeHtmlEntities(value);

    if (type === 'state') {
      filterState.state = filterState.state.filter(v => v !== value);
      updateSelectedPills('tournamentStateSelectedPills', filterState.state, 'state');
      updateDynamicCityOptions();
    } else if (type === 'city') {
      filterState.city = filterState.city.filter(v => v !== value);
      updateSelectedPills('tournamentCitySelectedPills', filterState.city, 'city');
    } else if (type === 'rating') {
      filterState.rating = filterState.rating.filter(v => v !== value);
      updateSelectedPills('tournamentRatingSelectedPills', filterState.rating, 'rating');
    } else if (type === 'table_size') {
      filterState.table_size = filterState.table_size.filter(v => v !== value);
      updateSelectedPills('tournamentSizeSelectedPills', filterState.table_size, 'table_size');
    }

    updateChipDisplay();
    updateHiddenInputs();
    applyFilters();
  };

  // Update chip display (for button text and active state)
  const updateChipDisplay = () => {
    // Only manage .active class - content is managed by updateSelectedPills()

    // Update state chip .active class
    if (stateChip) {
      if (filterState.state.length > 0) {
        stateChip.classList.add('active');
      } else {
        stateChip.textContent = 'State';
        stateChip.classList.remove('active');
      }
    }

    // Update city chip .active class
    if (cityChip) {
      if (filterState.city.length > 0) {
        cityChip.classList.add('active');
      } else {
        cityChip.textContent = 'City';
        cityChip.classList.remove('active');
      }
    }

    // Update rating chip .active class
    if (ratingChip) {
      if (filterState.rating.length > 0) {
        ratingChip.classList.add('active');
      } else {
        ratingChip.textContent = 'Rating';
        ratingChip.classList.remove('active');
      }
    }

    // Update table size chip .active class
    if (sizeChip) {
      if (filterState.table_size.length > 0) {
        sizeChip.classList.add('active');
      } else {
        sizeChip.textContent = 'Table Size';
        sizeChip.classList.remove('active');
      }
    }

    renderActiveSearchTag();
  };

  const renderActiveSearchTag = () => {
    if (!activeFiltersContainer) return;

    activeFiltersContainer.innerHTML = '';
    const searchQuery = String(filterState.q || '').trim();
    if (searchQuery.length < 3) return;

    const tag = document.createElement('div');
    tag.className = 'active-filter-tag';

    const label = document.createElement('span');
    label.textContent = `Search: "${searchQuery}"`;

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'remove-filter';
    clearBtn.setAttribute('aria-label', 'Clear search filter');
    clearBtn.textContent = '\u00d7';
    clearBtn.addEventListener('click', (event) => {
      event.preventDefault();
      if (heroSearchInput) heroSearchInput.value = '';
      filterState.q = '';
      filterState.page = 1;
      renderActiveSearchTag();
      applyFilters({ keepPage: false, activateClientFiltering: true, updateUrl: true });
    });

    tag.appendChild(label);
    tag.appendChild(clearBtn);
    activeFiltersContainer.appendChild(tag);
  };

  const getSelectedFilterCount = () => {
    let count = 0;
    count += filterState.state.length;
    count += filterState.city.length;
    count += filterState.rating.length;
    count += filterState.table_size.length;
    if (filterState.buy_in_min) count += 1;
    if (filterState.buy_in_max) count += 1;
    return count;
  };

  const updateMoreButtonCount = () => {
    if (!moreBtn) return;
    const count = getSelectedFilterCount();
    const desktopLabel = moreBtn.querySelector('.desktop-only');
    const countLabel = moreBtn.querySelector('#filterCount');

    // Toggle active class based on filter count
    if (count > 0) {
      moreBtn.classList.add('active');
      moreBtn.classList.add('has-selected-filters');
      moreBtn.setAttribute('data-filter-count', String(count));
    } else {
      moreBtn.classList.remove('active');
      moreBtn.classList.remove('has-selected-filters');
      moreBtn.removeAttribute('data-filter-count');
    }

    if (desktopLabel) {
      desktopLabel.textContent = 'More';
    }

    if (countLabel) {
      countLabel.textContent = count > 0 ? ` (${count})` : '';
    }

    moreBtn.setAttribute('aria-label', count > 0 ? `More filters (${count} selected)` : 'More filters');
  };

  const updateSortButtonVisual = (sortValue) => {
    if (!sortButton) return;
    const icon = document.getElementById('tournamentSortButtonIcon');
    if (!icon) return;

    const calendarArrowUp = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-calendar-arrow-up"><path d="m14 18 4-4 4 4"></path><path d="M16 2v4"></path><path d="M18 22v-8"></path><path d="M21 11.343V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9"></path><path d="M3 10h18"></path><path d="M8 2v4"></path></svg>';
    const calendarArrowDown = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-calendar-arrow-down"><path d="m14 18 4 4 4-4"></path><path d="M16 2v4"></path><path d="M18 14v8"></path><path d="M21 11.354V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7.343"></path><path d="M3 10h18"></path><path d="M8 2v4"></path></svg>';
    const mapPinSearch = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-search"><path d="M 12.248 21.969 a 1 1 0 0 1 -0.849 -0.17 C 9.539 20.193 4 14.993 4 10 a 8 8 0 0 1 16 0 C 20 10.42 19.961 10.841 19.888 11.262"></path><path d="m22 22-1.88-1.88"></path><circle cx="12" cy="10" r="3"></circle><circle cx="18" cy="18" r="3"></circle></svg>';

    if (sortValue === 'date-distance') {
      sortButton.classList.add('is-distance');
      icon.innerHTML = `<span class="sort-button-icon-combo">${calendarArrowUp}<span class="sort-icon-plus">+</span>${mapPinSearch}</span>`;
    } else if (sortValue === 'distance') {
      sortButton.classList.add('is-distance');
      icon.innerHTML = mapPinSearch;
    } else if (sortValue === 'past') {
      sortButton.classList.remove('is-distance');
      icon.innerHTML = calendarArrowDown;
    } else {
      sortButton.classList.remove('is-distance');
      icon.innerHTML = calendarArrowUp;
    }
  };

  const syncFilterStateFromHiddenInputs = () => {
    filterState.state = stateHidden && stateHidden.value ? stateHidden.value.split(',').filter(Boolean) : [];
    filterState.city = cityHidden && cityHidden.value ? cityHidden.value.split(',').filter(Boolean) : [];
    filterState.rating = ratingHidden && ratingHidden.value ? ratingHidden.value.split(',').filter(Boolean) : [];
    filterState.table_size = tableSizeHidden && tableSizeHidden.value ? tableSizeHidden.value.split(',').filter(Boolean) : [];
    filterState.buy_in_min = (buyInMinHidden && buyInMinHidden.value) ? String(buyInMinHidden.value).trim() : '';
    filterState.buy_in_max = (buyInMaxHidden && buyInMaxHidden.value) ? String(buyInMaxHidden.value).trim() : '';
  };

  const updateBrowserUrl = () => {
    if (!window.history || typeof window.history.replaceState !== 'function') return;

    const params = new URLSearchParams();

    if (filterState.state.length > 0) params.set('state', filterState.state.join(','));
    if (filterState.city.length > 0) params.set('city', filterState.city.join(','));
    if (filterState.rating.length > 0) params.set('rating', filterState.rating.join(','));
    if (filterState.table_size.length > 0) params.set('table_size', filterState.table_size.join(','));
    if (filterState.buy_in_min) params.set('buy_in_min', filterState.buy_in_min);
    if (filterState.buy_in_max) params.set('buy_in_max', filterState.buy_in_max);
    if (filterState.q && filterState.q.length >= 3) params.set('q', filterState.q);
    if (filterState.sort && filterState.sort !== 'date') params.set('sort', filterState.sort);
    if (filterState.sort === 'past') params.set('status', 'past');

    const usesDistance = filterState.sort === 'distance' || filterState.sort === 'date-distance';
    if (usesDistance && filterState.latitude !== null && filterState.longitude !== null) {
      params.set('latitude', filterState.latitude);
      params.set('longitude', filterState.longitude);
      if (filterState.radius_miles) {
        params.set('radius_miles', filterState.radius_miles);
      }
    }
    if (Number.isFinite(Number(filterState.page)) && Number(filterState.page) > 1) {
      params.set('tpage', String(filterState.page));
    }

    const query = params.toString();
    const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState({}, '', nextUrl);
  };

  const buildTournamentPagingUrl = (pageNumber) => {
    const params = new URLSearchParams();

    if (filterState.state.length > 0) params.set('state', filterState.state.join(','));
    if (filterState.city.length > 0) params.set('city', filterState.city.join(','));
    if (filterState.rating.length > 0) params.set('rating', filterState.rating.join(','));
    if (filterState.table_size.length > 0) params.set('table_size', filterState.table_size.join(','));
    if (filterState.buy_in_min) params.set('buy_in_min', filterState.buy_in_min);
    if (filterState.buy_in_max) params.set('buy_in_max', filterState.buy_in_max);
    if (filterState.q && filterState.q.length >= 3) params.set('q', filterState.q);
    if (filterState.sort && filterState.sort !== 'date') params.set('sort', filterState.sort);
    if (filterState.sort === 'past') params.set('status', 'past');

    const usesDistance = filterState.sort === 'distance' || filterState.sort === 'date-distance';
    if (usesDistance && filterState.latitude !== null && filterState.longitude !== null) {
      params.set('latitude', filterState.latitude);
      params.set('longitude', filterState.longitude);
      if (filterState.radius_miles) {
        params.set('radius_miles', filterState.radius_miles);
      }
    }

    if (Number(pageNumber) > 1) {
      params.set('tpage', String(pageNumber));
    }

    const query = params.toString();
    return query ? `${window.location.pathname}?${query}` : window.location.pathname;
  };

  const syncDesktopPagination = ({ totalPages, currentPage }) => {
    const paginationRoot = document.getElementById('tournament-pagination');
    if (!paginationRoot) return;

    const safeTotalPages = Math.max(1, Number(totalPages) || 1);
    const safeCurrentPage = Math.min(Math.max(1, Number(currentPage) || 1), safeTotalPages);

    if (safeTotalPages <= 1) {
      paginationRoot.innerHTML = '';
      return;
    }

    const fragments = [];

    if (safeCurrentPage > 1) {
      const prevPage = safeCurrentPage - 1;
      fragments.push(`<a href="${buildTournamentPagingUrl(prevPage)}" class="pagination-button" data-page="${prevPage}">Previous</a>`);
    } else {
      fragments.push('<button class="pagination-button" disabled>Previous</button>');
    }

    const pageStart = safeCurrentPage - 2;
    const pageEnd = safeCurrentPage + 2;
    let hasInsertedLeadingEllipsis = false;
    let hasInsertedTrailingEllipsis = false;

    for (let pageIndex = 1; pageIndex <= safeTotalPages; pageIndex += 1) {
      const isVisibleWindow = pageIndex === 1 || pageIndex === safeTotalPages || (pageIndex >= pageStart && pageIndex <= pageEnd);
      if (isVisibleWindow) {
        const isActive = pageIndex === safeCurrentPage;
        fragments.push(`<a class="pagination-link${isActive ? ' is-active' : ''}" href="${buildTournamentPagingUrl(pageIndex)}" data-page="${pageIndex}">${pageIndex}</a>`);
        continue;
      }

      if (pageIndex < pageStart && !hasInsertedLeadingEllipsis) {
        fragments.push('<span class="pagination-ellipsis">...</span>');
        hasInsertedLeadingEllipsis = true;
      }

      if (pageIndex > pageEnd && !hasInsertedTrailingEllipsis) {
        fragments.push('<span class="pagination-ellipsis">...</span>');
        hasInsertedTrailingEllipsis = true;
      }
    }

    if (safeCurrentPage < safeTotalPages) {
      const nextPage = safeCurrentPage + 1;
      fragments.push(`<a href="${buildTournamentPagingUrl(nextPage)}" class="pagination-button" data-page="${nextPage}">Next</a>`);
    } else {
      fragments.push('<button class="pagination-button" disabled>Next</button>');
    }

    paginationRoot.innerHTML = fragments.join('');
  };

  function updateHiddenInputs() {
    if (stateHidden) stateHidden.value = filterState.state.join(',');
    if (cityHidden) cityHidden.value = filterState.city.join(',');
    if (ratingHidden) ratingHidden.value = filterState.rating.join(',');
    if (tableSizeHidden) tableSizeHidden.value = filterState.table_size.join(',');
    if (buyInMinHidden) buyInMinHidden.value = filterState.buy_in_min;
    if (buyInMaxHidden) buyInMaxHidden.value = filterState.buy_in_max;
    updateMoreButtonCount();
  }

  window.refreshTournamentFilterUI = function () {
    syncFilterStateFromHiddenInputs();
    updateSelectedPills('tournamentStateSelectedPills', filterState.state, 'state');
    updateSelectedPills('tournamentCitySelectedPills', filterState.city, 'city');
    updateSelectedPills('tournamentRatingSelectedPills', filterState.rating, 'rating');
    updateSelectedPills('tournamentSizeSelectedPills', filterState.table_size, 'table_size');
    updateCityOptions();
    updateChipDisplay();
    updateMoreButtonCount();
  };

  // ============================================================================
  // GEOLOCATION & FILTER STATE
  // ============================================================================

  // Global filter state (stores coordinates and sort for conditional API call)
  const filterState = {
    state: [],      // Multi-select array
    city: [],       // Multi-select array
    rating: [],     // Multi-select array
    table_size: [], // Multi-select array
    buy_in_min: '',
    buy_in_max: '',
    q: '',
    page: 1,
    latitude: null,
    longitude: null,
    radius_miles: '25',
    sort: 'date'
  };

  const normalizeRadiusMiles = (value) => {
    const normalized = String(value || '').trim();
    return ['10', '25', '50', '100'].includes(normalized) ? normalized : '25';
  };

  const syncTournamentRadiusControl = () => {
    if (!radiusSelect) return;
    const nextValue = normalizeRadiusMiles(filterState.radius_miles);
    if (radiusSelect.value !== nextValue) {
      radiusSelect.value = nextValue;
    }
  };

  window.setTournamentRadiusFilter = function (value) {
    filterState.radius_miles = normalizeRadiusMiles(value);
    syncTournamentRadiusControl();

    const hasCoordinates = filterState.latitude !== null && filterState.longitude !== null;
    const usesDistance = filterState.sort === 'distance' || filterState.sort === 'date-distance';
    if (hasCoordinates && !usesDistance) {
      window.setTournamentSort('distance');
      return;
    }

    applyFilters();
  };

  // Match profile PLP behavior: keep Liquid-rendered cards/pagination by default,
  // and switch to JS/API mode only after filters are active or user interaction.
  let isClientFilteringActive = false;
  let currentViewMode = 'grid';
  const MOBILE_INFINITE_BATCH_SIZE = 24;
  const MOBILE_INFINITE_TRIGGER_PX = 24;
  const mobileInfiniteState = {
    isLoading: false,
    hasMore: true
  };
  let infiniteLoaderEl = null;

  const isMobileGridMode = () => window.innerWidth <= 768 && currentViewMode === 'grid';

  const resetMobileInfiniteState = () => {
    mobileInfiniteState.isLoading = false;
    mobileInfiniteState.hasMore = true;
    if (infiniteLoaderEl) {
      infiniteLoaderEl.style.display = 'none';
    }
  };

  const ensureInfiniteLoader = () => {
    if (infiniteLoaderEl) return infiniteLoaderEl;
    if (!grid || !grid.parentNode) return null;
    const loader = document.createElement('div');
    loader.id = 'tournamentInfiniteLoader';
    loader.className = 'infinite-scroll-loading';
    loader.textContent = 'Loading more tournaments...';
    loader.style.display = 'none';
    grid.insertAdjacentElement('afterend', loader);
    infiniteLoaderEl = loader;
    return loader;
  };

  const setInfiniteLoaderVisible = (visible) => {
    const loader = ensureInfiniteLoader();
    if (!loader) return;
    loader.style.display = visible ? 'flex' : 'none';
  };

  const setViewMode = (mode) => {
    const previousViewMode = currentViewMode;
    currentViewMode = mode === 'map' ? 'map' : 'grid';

    if (gridViewBtn) {
      gridViewBtn.classList.toggle('active', currentViewMode === 'grid');
      gridViewBtn.setAttribute('aria-pressed', currentViewMode === 'grid' ? 'true' : 'false');
    }
    if (mapViewBtn) {
      mapViewBtn.classList.toggle('active', currentViewMode === 'map');
      mapViewBtn.setAttribute('aria-pressed', currentViewMode === 'map' ? 'true' : 'false');
    }

    if (grid) {
      grid.classList.toggle('map-overlay-grid', false);
      grid.style.display = currentViewMode === 'map' ? 'none' : '';
    }
    if (mapPanel) {
      mapPanel.style.display = currentViewMode === 'map' ? 'block' : 'none';
    }
    wrapper.classList.toggle('map-overlay-mode', currentViewMode === 'map');

    if (currentViewMode === 'map') {
      if (previousViewMode !== 'map') {
        filterState.radius_miles = '25';
        syncTournamentRadiusControl();
      }

      if (!mapTabEnabled) {
        if (mapPanel) {
          mapPanel.classList.add('map-coming-soon');
          mapPanel.innerHTML = '<h3>Map view coming soon</h3><p>Map mode for tournaments is on the way. Stay on Grid view for now.</p>';
        }
      } else {
        if (mapPanel && !mapPanel.querySelector('#tournamentMapCanvas')) {
          mapPanel.classList.remove('map-coming-soon');
          mapPanel.innerHTML = '<div id="tournamentMapCanvas" class="tournament-map-canvas" role="application" aria-label="Tournament map"></div><div id="tournamentMapEmptyState" class="tournament-map-empty-state" style="display: none;"></div>';
        }

        if (filterState.sort !== 'distance') {
          window.setTournamentSort('distance');
        } else if (previousViewMode !== 'map') {
          applyFilters();
        }

        updateTournamentMapMarkers(latestRenderedTournaments);
        if (tournamentMap) {
          window.setTimeout(() => tournamentMap.resize(), 0);
        }
      }
    }
  };

  window.setTournamentViewMode = setViewMode;

  const hasActiveFilters = () => {
    return (
      filterState.state.length > 0 ||
      filterState.city.length > 0 ||
      filterState.rating.length > 0 ||
      filterState.table_size.length > 0 ||
      Boolean(filterState.buy_in_min) ||
      Boolean(filterState.buy_in_max) ||
      Boolean(filterState.q && filterState.q.trim().length >= 3) ||
      (filterState.sort && filterState.sort !== 'date') ||
      (filterState.latitude !== null && filterState.longitude !== null)
    );
  };

  window.setGeoFromBrowser = function (options = {}) {
    const fromSort = Boolean(options && options.fromSort);
    // Toggle if already active
    if (!fromSort && useLocationBtn && useLocationBtn.classList.contains('active')) {
      const wasDistanceSort = filterState.sort === 'distance';
      useLocationBtn.classList.remove('active');
      filterState.latitude = null;
      filterState.longitude = null;
      if (latitudeInput) latitudeInput.value = '';
      if (longitudeInput) longitudeInput.value = '';
      if (wasDistanceSort) {
        filterState.sort = 'date';
        if (sortSelect) sortSelect.value = 'date';
        if (sortLabel) sortLabel.textContent = 'Upcoming';
        updateSortButtonVisual('date');
      }
      updateChipDisplay();
      applyFilters();
      return;
    }

    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = String(position.coords.latitude);
        const lng = String(position.coords.longitude);
        // Store coordinates and switch to Distance so Near Me immediately has visible effect.
        filterState.latitude = lat;
        filterState.longitude = lng;
        if (latitudeInput) latitudeInput.value = lat;
        if (longitudeInput) longitudeInput.value = lng;
        filterState.sort = 'distance';
        if (sortSelect) sortSelect.value = 'distance';
        if (sortLabel) sortLabel.textContent = 'Distance';
        updateSortButtonVisual('distance');
        if (useLocationBtn) useLocationBtn.classList.add('active');
        updateChipDisplay();
        applyFilters();
      },
      () => { },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  // ============================================================================
  // SORT
  // ============================================================================

  window.toggleTournamentSortDropdown = function () {
    const sortSection = document.querySelector('.sort-filter-section');
    if (sortDropdown) {
      const isHidden = window.getComputedStyle(sortDropdown).display === 'none';
      const nextState = isHidden ? 'block' : 'none';
      sortDropdown.style.display = nextState;
      if (sortSection) {
        sortSection.classList.toggle('sort-open', nextState === 'block');
      }
    }
  };

  window.setTournamentSort = function (sortValue) {
    const needsGeoCapture = sortValue === 'distance' && (filterState.latitude === null || filterState.longitude === null);
    if (needsGeoCapture) {
      window.setGeoFromBrowser({ fromSort: true });
      return;
    }

    filterState.sort = sortValue;
    if (sortSelect) sortSelect.value = sortValue;

    const labels = {
      date: 'Upcoming',
      past: 'Past',
      distance: 'Distance',
      'date-distance': 'Upcoming + Distance'
    };

    if (sortLabel) sortLabel.textContent = labels[sortValue] || 'Sort';
    updateSortButtonVisual(sortValue);

    // Update checkmarks
    if (sortDropdown) {
      sortDropdown.querySelectorAll('.sort-option').forEach((opt) => {
        const checkbox = opt.querySelector('.lucide-check');
        const isActive = opt.getAttribute('data-sort-value') === sortValue;
        if (checkbox) checkbox.style.opacity = isActive ? '1' : '0';
      });
    }

    if (sortDropdown) sortDropdown.style.display = 'none';
    const sortSection = document.querySelector('.sort-filter-section');
    if (sortSection) {
      sortSection.classList.remove('sort-open');
    }

    if (useLocationBtn) {
      if (sortValue === 'distance' || sortValue === 'date-distance') {
        useLocationBtn.classList.add('active');
      } else {
        useLocationBtn.classList.remove('active');
      }
    }

    applyFilters();
  };

  // ============================================================================
  // HERO SEARCH
  // ============================================================================

  const handleHeroSearch = debounce(() => {
    applyFilters();
  }, 350);

  // ============================================================================
  // API & RENDERING
  // ============================================================================

  const fetchWithFilters = ({ append = false } = {}) => {
    const endpoint = '/a/app';
    const usesDistanceSort = filterState.sort === 'distance' || filterState.sort === 'date-distance';
    const shouldFetchAllForMap = (currentViewMode === 'map' || usesDistanceSort) && !append;

    const buildParams = (page, perPage) => {
      const params = new URLSearchParams();
      params.set('app', 'tournaments');
      params.set('listing_slug', listingSlug);
      params.set('page', String(page));
      params.set('per_page', String(perPage));

      if (filterState.state.length > 0) params.set('state', filterState.state.join(','));
      if (filterState.city.length > 0) params.set('city', filterState.city.join(','));
      if (filterState.rating.length > 0) params.set('rating', filterState.rating.join(','));
      if (filterState.table_size.length > 0) params.set('table_size', filterState.table_size.join(','));

      if (filterState.buy_in_min) params.set('buy_in_min', filterState.buy_in_min);
      if (filterState.buy_in_max) params.set('buy_in_max', filterState.buy_in_max);
      if (filterState.q && filterState.q.length >= 3) {
        params.set('q', filterState.q);
      }
      const usesDistance = filterState.sort === 'distance' || filterState.sort === 'date-distance';
      const useServerDistanceSort = false;

      if (filterState.sort === 'past') {
        params.set('sort', 'date');
        params.set('status', 'past');
      } else {
        const sortForRequest = useServerDistanceSort
          ? filterState.sort
          : (usesDistance ? 'date' : filterState.sort);
        if (sortForRequest) params.set('sort', sortForRequest);
        params.set('status', 'upcoming');
      }

      if (useServerDistanceSort && filterState.latitude !== null && filterState.longitude !== null) {
        params.set('latitude', filterState.latitude);
        params.set('longitude', filterState.longitude);
        if (filterState.radius_miles) {
          params.set('radius_miles', filterState.radius_miles);
        }
      }

      return params;
    };

    const fetchPage = (page, perPage) => {
      const params = buildParams(page, perPage);
      const fetchUrl = `${endpoint}?${params.toString()}`;

      console.log('🎯 Fetching tournaments:', fetchUrl);

      return fetch(fetchUrl, { headers: { Accept: 'application/json' } })
        .then(async (response) => {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(`Request failed (${response.status}): ${text.slice(0, 200)}`);
          }
          return response.json();
        });
    };

    if (!shouldFetchAllForMap) {
      return fetchPage(filterState.page || 1, MOBILE_INFINITE_BATCH_SIZE)
        .catch((err) => {
          console.error('Fetch error:', err);
          return null;
        });
    }

    const MAP_MODE_PAGE_SIZE = 250;
    const MAX_MAP_MODE_PAGES = 20;

    return (async () => {
      const allItems = [];
      let page = 1;
      let totalPages = 1;
      let lastResponse = null;

      do {
        const response = await fetchPage(page, MAP_MODE_PAGE_SIZE);
        lastResponse = response;

        const pageItems = Array.isArray(response?.tournaments) ? response.tournaments : [];
        allItems.push(...pageItems);

        const reportedTotalPages = Number(response?.pagination?.totalPages);
        if (Number.isFinite(reportedTotalPages) && reportedTotalPages > 0) {
          totalPages = reportedTotalPages;
        } else {
          totalPages = pageItems.length >= MAP_MODE_PAGE_SIZE ? page + 1 : page;
        }

        page += 1;
      } while (page <= totalPages && page <= MAX_MAP_MODE_PAGES);

      if (!lastResponse) {
        return null;
      }

      return {
        ...lastResponse,
        tournaments: allItems,
        pagination: {
          ...(lastResponse.pagination || {}),
          page: 1,
          totalPages: 1,
          total: allItems.length,
        },
      };
    })().catch((err) => {
      console.error('Fetch error:', err);
      return null;
    });
  };

  const revealInitialGrid = () => {
    if (!grid) return;
    grid.classList.remove('js-initial-pending');
  };

  const toStartTimestamp = (value) => {
    const ms = Date.parse(String(value || ''));
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
  };

  const sortGridCardsByStartTs = (direction = 'asc') => {
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('.tournament-card'));
    if (cards.length < 2) return;

    cards.sort((a, b) => {
      const aTs = Number(a.getAttribute('data-start-ts') || '0');
      const bTs = Number(b.getAttribute('data-start-ts') || '0');
      if (direction === 'desc') return bTs - aTs;
      return aTs - bTs;
    });

    cards.forEach((card) => grid.appendChild(card));
  };

  const renderCards = (items, totalCount, options = {}) => {
    const { append = false } = options;
    if (!grid) return;
    latestRenderedTournaments = Array.isArray(items) ? items : [];
    revealInitialGrid();
    if (!append) {
      grid.innerHTML = '';
    }

    if (!items || items.length === 0) {
      if (!append) {
        grid.innerHTML = '<div class="no-results">No tournaments found.</div>';
      }
      if (currentViewMode === 'map') {
        updateTournamentMapMarkers([]);
      }
      return;
    }

    items.forEach((tournament) => {
      const card = document.createElement('div');
      const isCancelled = String(tournament.status || '').toLowerCase() === 'cancelled';
      card.className = `tournament-card${isCancelled ? ' is-cancelled' : ''}`;
      const startTs = toStartTimestamp(tournament.tournamentStartDate || tournament.tournament_start_date);
      card.setAttribute('data-start-ts', String(startTs));

      const imageUrl =
        (Array.isArray(tournament.galleryUrls) && tournament.galleryUrls[0]) ||
        (Array.isArray(tournament.profileGalleryUrls) && tournament.profileGalleryUrls[0]) ||
        (tournament.profile && tournament.profile.logoUrl) ||
        '';
      const tournamentSpec = getSpec(tournament);
      const dateLabel = formatTournamentDateLine(tournament.tournamentStartDate, tournament.tournamentEndDate);
      const locationAddress = [tournament.locationCity, getStateDisplayName(tournament.locationState), tournament.postalCode].filter(Boolean).join(', ');
      const tableLabel = getTableLabel(tournamentSpec);
      const tableLabelDisplay = formatFeetLabel(tableLabel);
      const gameLabel = getCombinedGameLabel(tournamentSpec);
      const buyInLabel = tournament.buyIn != null ? `$${tournament.buyIn}` : '--';
      const directionsUrl = buildDirectionsUrl(tournament);

      const badgeLabel = String(tournament.series || '').trim();

      card.innerHTML = `
        ${isCancelled ? '<div class="tournament-card__cancelled-overlay">Cancelled</div>' : ''}
        <div class="tournament-card__media">
          ${badgeLabel ? `<div class="tournament-card__badge">${escapeHtml(badgeLabel)}</div>` : ''}
          ${imageUrl ? `<img src="${imageUrl}" alt="${tournament.title}" loading="lazy" />` : '<div class="tournament-card__placeholder"></div>'}
        </div>
        <div class="tournament-card__body">
          <h3 class="tournament-card__title">${tournament.title}</h3>
          <div class="tournament-card__meta">
            <div>${dateLabel}</div>
            <div class="tournament-card__location-name">${escapeHtml(tournament.locationName || '')}</div>
            <div class="tournament-card__address">${escapeHtml(locationAddress)}</div>
            ${tournament.distance ? `<div>${tournament.distance.toFixed(1)} mi</div>` : ''}
          </div>
          <div class="tournament-card__stats-grid">
            <div class="tournament-stat-box"><div class="tournamenta-stat-label">Buy-in</div><div class="tournament-stat-value">${escapeHtml(buyInLabel)}</div></div>
            <div class="tournament-stat-box"><div class="tournament-stat-label">Game</div><div class="tournament-stat-value">${escapeHtml(gameLabel)}</div></div>
            <div class="tournament-stat-box"><div class="tournament-stat-label">Table</div><div class="tournament-stat-value">${escapeHtml(tableLabelDisplay)}</div></div>
          </div>
          <div class="tournament-card__actions">
            <a class="tournament-card__direction-btn" target="_blank" rel="noopener" href="${directionsUrl}" aria-label="Get directions">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>
            </a>
            <a class="tournament-card__details-btn" href="/pages/${listingSlug}/${tournament.slug}">View Details</a>
          </div>
        </div>
      `;

      grid.appendChild(card);
    });

    if (filterState.sort === 'date') {
      sortGridCardsByStartTs('asc');
    } else if (filterState.sort === 'past') {
      sortGridCardsByStartTs('desc');
    }

    if (currentViewMode === 'map') {
      updateTournamentMapMarkers(latestRenderedTournaments);
    }
  };

  const applyFilters = (options = {}) => {
    const { keepPage = false, activateClientFiltering = true, updateUrl = true, append = false } = options;

    if (activateClientFiltering) {
      isClientFilteringActive = true;
    }

    if (!keepPage) {
      filterState.page = 1;
    }

    if (!append) {
      resetMobileInfiniteState();
    }

    // Sync search query and sort to filterState before fetching
    filterState.q = (heroSearchInput && heroSearchInput.value) || '';
    filterState.sort = (sortSelect && sortSelect.value) || 'date';
    renderActiveSearchTag();
    updateSortButtonVisual(filterState.sort);

    if (useLocationBtn) {
      if (filterState.sort === 'distance' && filterState.latitude !== null && filterState.longitude !== null) {
        useLocationBtn.classList.add('active');
      } else {
        useLocationBtn.classList.remove('active');
      }
    }

    updateMoreButtonCount();
    if (updateUrl) {
      updateBrowserUrl();
    }

    if (append && isMobileGridMode()) {
      setInfiniteLoaderVisible(true);
    }

    return fetchWithFilters({ append })
      .then((data) => {
        if (!data) {
          // Keep SSR cards when initial fetch fails; just reveal them.
          revealInitialGrid();
          return;
        }

        const items = Array.isArray(data && data.tournaments) ? data.tournaments : [];
        let visibleItems = items.filter((item) => {
          const status = String(item?.status || '').trim().toLowerCase();
          return status !== 'cancelled' && status !== 'deleted';
        });
        visibleItems = applyClientDistanceFilter(visibleItems);

        const usesDistanceSort = filterState.sort === 'distance' || filterState.sort === 'date-distance';
        const clientPagedGrid = currentViewMode === 'grid' && usesDistanceSort;

        let renderItems = visibleItems;
        if (clientPagedGrid) {
          const pageNum = Math.max(1, Number(filterState.page) || 1);
          const start = (pageNum - 1) * MOBILE_INFINITE_BATCH_SIZE;
          const end = start + MOBILE_INFINITE_BATCH_SIZE;
          renderItems = visibleItems.slice(start, end);
        }

        const totalCount = visibleItems.length;
        renderCards(renderItems, totalCount, { append });

        let nextTotalPages = Number(data?.pagination?.totalPages);
        if (clientPagedGrid) {
          nextTotalPages = Math.max(1, Math.ceil(visibleItems.length / MOBILE_INFINITE_BATCH_SIZE));
        } else if (!Number.isFinite(nextTotalPages) || nextTotalPages < 1) {
          nextTotalPages = Math.max(1, Math.ceil(items.length / MOBILE_INFINITE_BATCH_SIZE));
        }

        syncDesktopPagination({
          totalPages: nextTotalPages,
          currentPage: Number(filterState.page) || 1,
        });

        if (currentViewMode === 'map') {
          mobileInfiniteState.hasMore = false;
        } else if (clientPagedGrid) {
          const pageNum = Math.max(1, Number(filterState.page) || 1);
          const end = pageNum * MOBILE_INFINITE_BATCH_SIZE;
          mobileInfiniteState.hasMore = visibleItems.length > end;
        } else {
          const currentPage = Number(filterState.page) || 1;
          const totalPages = Number(data?.pagination?.totalPages);
          if (Number.isFinite(totalPages) && totalPages > 0) {
            mobileInfiniteState.hasMore = currentPage < totalPages;
          } else {
            mobileInfiniteState.hasMore = items.length >= MOBILE_INFINITE_BATCH_SIZE;
          }
        }
      })
      .finally(() => {
        if (append && isMobileGridMode()) {
          setInfiniteLoaderVisible(false);
        }
      });
  };

  window.applyFilters = applyFilters;

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  const initFromUrl = () => {
    const params = getParams();

    const pageFromUrl = Number.parseInt(params.get('tpage') || params.get('page') || '1', 10);
    filterState.page = Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;

    // Initialize multi-select arrays from URL params (comma-separated)
    if (params.get('state')) {
      filterState.state = params.get('state').split(',').filter(Boolean);
      updateSelectedPills('tournamentStateSelectedPills', filterState.state, 'state');
    }
    if (params.get('city')) {
      filterState.city = params.get('city').split(',').filter(Boolean);
      updateSelectedPills('tournamentCitySelectedPills', filterState.city, 'city');
    }
    if (params.get('rating')) {
      filterState.rating = params.get('rating').split(',').filter(Boolean);
      updateSelectedPills('tournamentRatingSelectedPills', filterState.rating, 'rating');
    }
    if (params.get('table_size')) {
      filterState.table_size = params.get('table_size').split(',').filter(Boolean);
      updateSelectedPills('tournamentSizeSelectedPills', filterState.table_size, 'table_size');
    }

    if (params.get('buy_in_min')) {
      filterState.buy_in_min = params.get('buy_in_min');
    }
    if (params.get('buy_in_max')) {
      filterState.buy_in_max = params.get('buy_in_max');
    }

    if (params.get('latitude')) {
      filterState.latitude = parseFloat(params.get('latitude'));
      if (useLocationBtn) useLocationBtn.classList.add('active');
    }
    if (params.get('longitude')) {
      filterState.longitude = parseFloat(params.get('longitude'));
    }
    if (params.get('sort')) {
      filterState.sort = params.get('sort');
      if (sortSelect) sortSelect.value = params.get('sort');
      if (sortLabel) {
        const labels = { date: 'Upcoming', past: 'Past', distance: 'Distance', 'date-distance': 'Upcoming + Distance' };
        sortLabel.textContent = labels[filterState.sort] || 'Sort';
      }
      updateSortButtonVisual(filterState.sort);
      if (useLocationBtn) {
        if (filterState.sort === 'distance' || filterState.sort === 'date-distance') {
          useLocationBtn.classList.add('active');
        } else {
          useLocationBtn.classList.remove('active');
        }
      }
    }
    if (params.get('status') === 'past') {
      filterState.sort = 'past';
      if (sortSelect) sortSelect.value = 'past';
      if (sortLabel) sortLabel.textContent = 'Past';
      updateSortButtonVisual('past');
    }
    if (params.get('radius_miles')) {
      filterState.radius_miles = normalizeRadiusMiles(params.get('radius_miles'));
    }
    if (params.get('q')) {
      filterState.q = params.get('q');
      if (heroSearchInput) heroSearchInput.value = params.get('q');
    }

    syncTournamentRadiusControl();
    updateHiddenInputs();
    updateChipDisplay();
    updateMoreButtonCount();

    setViewMode('grid');

    // Hide dropdown results initially - they should only show on input/focus
    if (stateDropdown) stateDropdown.style.display = 'none';
    if (cityDropdown) cityDropdown.style.display = 'none';
    if (ratingDropdown) ratingDropdown.style.display = 'none';
    if (sizeDropdown) sizeDropdown.style.display = 'none';

    // Always use the JS/API path for correct sort and pagination
    applyFilters({ keepPage: true, activateClientFiltering: true, updateUrl: true });
  };

  const attachEvents = () => {
    if (radiusSelect) {
      radiusSelect.addEventListener('change', (event) => {
        const nextValue = event?.target?.value;
        window.setTournamentRadiusFilter(nextValue);
      });
    }

    if (heroSearchInput) {
      heroSearchInput.addEventListener('input', handleHeroSearch);
      heroSearchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyFilters();
        }
      });
    }

    document.addEventListener('click', (e) => {
      const paginationLink = e.target.closest('#tournament-pagination a[data-page]');
      if (!paginationLink) return;

      const pageValue = Number.parseInt(String(paginationLink.getAttribute('data-page') || ''), 10);
      if (!Number.isFinite(pageValue) || pageValue < 1) return;

      e.preventDefault();
      filterState.page = pageValue;
      applyFilters({ keepPage: true, activateClientFiltering: true, updateUrl: true });
    });

    // Sort button click is wired inline in Liquid (onclick="toggleTournamentSortDropdown()").
    // Do not add a second listener here, otherwise one click toggles twice.

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.filter-dropdown-wrapper')) {
        [stateContainer, cityContainer, ratingContainer, sizeContainer].forEach((c) => {
          if (c) c.style.display = 'none';
        });
      }

      const isInsideSort = e.target.closest('.sort-filter-section');
      if (sortDropdown && !isInsideSort) {
        sortDropdown.style.display = 'none';
        const sortSection = document.querySelector('.sort-filter-section');
        if (sortSection) {
          sortSection.classList.remove('sort-open');
        }
      }
    });

    const onScroll = debounce(() => {
      if (!isMobileGridMode()) return;
      if (mobileInfiniteState.isLoading || !mobileInfiniteState.hasMore) return;
      const docHeight = document.documentElement.scrollHeight;
      const maxScrollY = Math.max(0, docHeight - window.innerHeight);
      const triggerScrollY = Math.max(MOBILE_INFINITE_TRIGGER_PX, maxScrollY * 0.4);
      if (window.scrollY < triggerScrollY) return;

      mobileInfiniteState.isLoading = true;
      filterState.page = (Number(filterState.page) || 1) + 1;
      applyFilters({ keepPage: true, append: true, updateUrl: false })
        .finally(() => {
          mobileInfiniteState.isLoading = false;
        });
    }, 120);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  };

  // Initialize immediately (defer ensures DOM is ready)
  suppressBrowserAutofill([
    'heroSearchInput',
    'tournamentStateTypeahead',
    'tournamentCityTypeahead',
    'tournamentRatingTypeahead',
    'tournamentSizeTypeahead'
  ]);

  initFromUrl();
  attachEvents();
  setupMobileAddTournamentButton();
  setupStickyTopBar();

  // Keep Liquid-rendered pagination until filters activate JS mode.
  if (!isClientFilteringActive) {
    updateMoreButtonCount();
  }
})();
